import type { StockBalance } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Boxes, PackagePlus } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { flattenCategories } from "../../../../features/product-form.js";
import {
  formatStockQuantity,
  moneyExport,
  quantityExport,
} from "../../../../features/market/market-values.js";
import { OpeningStockDialog } from "../../../../features/market/opening-stock-dialog.js";
import {
  type StockFilters,
  stockQuery,
  useMarketPermissions,
  useStockBalances,
} from "../../../../features/market/queries.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  useMarketStations,
  WarehouseFilterField,
} from "../../../../features/market/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate, formatUnitPrice } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

const flag = z.enum(["true"]).optional().catch(undefined);
const searchSchema = z.object({
  ...marketSearchShape,
  warehouse: z.string().uuid().optional().catch(undefined),
  category: z.string().uuid().optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  belowMin: flag,
  negative: flag,
  hideZero: flag,
});

export const Route = createFileRoute("/_app/market/stok/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MarketStockRoute,
});

/**
 * Market stoğu (Prompt 16 — web): depo × ürün bakiyesi — eldeki, değer, hareketli ortalama birim maliyet,
 * min / max, raf yeri, son satış / kabul. Negatif bakiye ve asgari stok altı METİNLİ rozetle işaretlenir (renk tek
 * başına anlam taşımaz). Satırdan ürünün stok kartına gidilir. Açılış stoğu `market.receipt.manage` ister.
 */
function MarketStockRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketStock")}
      description={t("market.stock.description")}
      breadcrumb={[{ label: t("menu.marketStock") }]}
    >
      {({ companyId }) => <StockBody companyId={companyId} />}
    </MarketPage>
  );
}

function StockBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [opening, setOpening] = useState(false);
  const [searchText, setSearchText] = useState(search.q ?? "");
  const categories = useQuery({
    queryKey: queryKeys.productCategories,
    queryFn: async () => (await api.GET("/product-categories")).data!.items,
  });

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setSearch({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const filters: StockFilters = {
    stationId: station.stationId ?? undefined,
    warehouseId: station.stationId ? search.warehouse : undefined,
    categoryId: search.category,
    search: search.q,
    belowMin: search.belowMin === "true",
    negative: search.negative === "true",
    hideZero: search.hideZero === "true",
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useStockBalances(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<StockBalance>[]>(
    () => [
      {
        id: "product",
        header: t("market.fields.product"),
        hideable: false,
        exportValue: (row) => row.productName,
        cell: (row) => (
          <Link
            to="/market/stok/$productId"
            params={{ productId: row.productId }}
            search={{ station: row.stationId }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-medium">{row.productName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.productCode}</span>
          </Link>
        ),
      },
      {
        id: "productCode",
        header: t("market.fields.productCode"),
        defaultVisible: false,
        exportValue: (row) => row.productCode,
        cell: (row) => <span className="font-mono text-xs">{row.productCode}</span>,
      },
      {
        id: "location",
        header: t("market.fields.warehouse"),
        exportValue: (row) => `${row.stationName} · ${row.warehouseCode}`,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.warehouseCode}</span>
          </span>
        ),
      },
      {
        id: "category",
        header: t("market.fields.category"),
        defaultVisible: false,
        exportValue: (row) => row.categoryName,
        cell: (row) => row.categoryName ?? "—",
      },
      {
        id: "shelf",
        header: t("market.fields.shelfLocation"),
        exportValue: (row) => row.shelfLocation,
        cell: (row) =>
          row.shelfLocation === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="whitespace-nowrap">{row.shelfLocation}</span>
          ),
      },
      {
        id: "quantity",
        header: t("market.fields.onHand"),
        align: "right",
        hideable: false,
        exportValue: (row) => quantityExport(row.quantity),
        cell: (row) => (
          <span className="grid justify-items-end gap-1">
            <span className={`tabular font-medium ${row.isNegative ? "text-destructive" : ""}`}>
              {formatStockQuantity(row.quantity, row.unit)}
            </span>
            {row.isNegative ? (
              <StatusBadge tone="danger" className="whitespace-nowrap">
                {t("market.stock.negativeBadge")}
              </StatusBadge>
            ) : row.belowMin ? (
              <StatusBadge tone="warning" className="whitespace-nowrap">
                {t("market.stock.belowMinBadge")}
              </StatusBadge>
            ) : null}
          </span>
        ),
      },
      {
        id: "minMax",
        header: t("market.fields.minMax"),
        align: "right",
        exportValue: (row) =>
          `${row.minStock === null ? "" : formatStockQuantity(row.minStock)} / ${
            row.maxStock === null ? "" : formatStockQuantity(row.maxStock)
          }`,
        cell: (row) => (
          <span className="tabular text-sm whitespace-nowrap">
            {row.minStock === null ? "—" : formatStockQuantity(row.minStock)} /{" "}
            {row.maxStock === null ? "—" : formatStockQuantity(row.maxStock)}
          </span>
        ),
      },
      {
        id: "averageUnitCost",
        header: t("market.fields.averageUnitCost"),
        align: "right",
        exportValue: (row) => row.averageUnitCost,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{formatUnitPrice(row.averageUnitCost)}</span>
        ),
      },
      {
        id: "value",
        header: t("market.fields.stockValue"),
        align: "right",
        exportValue: (row) => moneyExport(row.value),
        cell: (row) => <MoneyText value={row.value} colorize className="font-medium" />,
      },
      {
        id: "lastSaleAt",
        header: t("market.fields.lastSaleAt"),
        exportValue: (row) => row.lastSaleAt,
        cell: (row) => <span className="tabular">{formatDate(row.lastSaleAt)}</span>,
      },
      {
        id: "lastReceiptAt",
        header: t("market.fields.lastReceiptAt"),
        exportValue: (row) => row.lastReceiptAt,
        cell: (row) => <span className="tabular">{formatDate(row.lastReceiptAt)}</span>,
      },
      {
        id: "lastCountedAt",
        header: t("market.fields.lastCountedAt"),
        defaultVisible: false,
        exportValue: (row) => row.lastCountedAt,
        cell: (row) => <span className="tabular">{formatDate(row.lastCountedAt)}</span>,
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  const hasFilters = Boolean(
    search.q ?? search.category ?? search.belowMin ?? search.negative ?? search.hideZero ?? search.warehouse,
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        <WarehouseFilterField
          companyId={companyId}
          stationId={station.stationId}
          value={search.warehouse}
          onChange={(warehouse) => setSearch({ warehouse })}
        />
        {permissions.canManageReceipts ? (
          <Button variant="outline" className="ms-auto" onClick={() => setOpening(true)}>
            <PackagePlus aria-hidden="true" />
            {t("market.opening.action")}
          </Button>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.stock.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Boxes />}
            title={hasFilters ? t("market.stock.emptyFilteredTitle") : t("market.stock.emptyTitle")}
            description={
              hasFilters ? t("market.stock.emptyFilteredDescription") : t("market.stock.emptyDescription")
            }
          />
        }
        filters={
          <>
            <FormField label={t("common.search")} className="w-60">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={t("market.stock.searchPlaceholder")}
                />
              )}
            </FormField>
            <FormField label={t("market.fields.category")} className="w-52">
              {(control) => (
                <Select
                  {...control}
                  value={search.category ?? ""}
                  onChange={(event) => setSearch({ category: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {flattenCategories(categories.data ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {"  ".repeat(category.depth) + category.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.belowMin === "true"}
                onChange={(event) => setSearch({ belowMin: event.target.checked ? "true" : undefined })}
              />
              {t("market.stock.belowMinOnly")}
            </label>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.negative === "true"}
                onChange={(event) => setSearch({ negative: event.target.checked ? "true" : undefined })}
              />
              {t("market.stock.negativeOnly")}
            </label>
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <Checkbox
                checked={search.hideZero === "true"}
                onChange={(event) => setSearch({ hideZero: event.target.checked ? "true" : undefined })}
              />
              {t("market.stock.hideZero")}
            </label>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<StockBalance>(
            async (cursor) =>
              (
                await api.GET("/market/stock", {
                  params: { query: stockQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "market-stok",
            sheetName: t("menu.marketStock"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManageReceipts ? (
        <OpeningStockDialog
          open={opening}
          onOpenChange={setOpening}
          companyId={companyId}
          stations={station.stations}
          defaultStationId={station.stationId}
        />
      ) : null}
    </>
  );
}
